/* ──────────────────────────────────────────────────────────────
   src/hooks/useReportJob.ts
   Hooks “plug & play” para los **informes** de profesores

   • useHorasProfesorReport()          →  POST /reports/horas-profesor
   • useHorasProfesorSemanalReport()   →  POST /reports/horas-profesor-semanal
   • useAlumnosProfesorReport()        →  POST /reports/profesor-alumnos

   Cada hook expone:
     • run(payload)         – encola el job  (payload depende de informe)
     • useStatus(jobId?)    – polling de /reports/:id/status
   ────────────────────────────────────────────────────────────── */

import { useMutation, useQuery, useQueryClient, QueryClient } from '@tanstack/react-query';

import {
  /* enqueue — dispara los jobs */
  enqueueHorasProfesor,
  enqueueHorasProfesorSemanal,
  enqueueHorasAsignaturaSemanal,
  enqueueAsignaturasProfesores,
  enqueueAsignaturasDemanda,
  enqueueAsignaturasAlumnosUnicos,
  enqueueAsignaturasRatioProfesor,
  enqueueAsignaturasCentroFormato,
  enqueueHorasProfesoresSemanales,
  enqueueAlumnosProfesor,
  /* polling genérico */
  getJobStatus,
  /* tipos que devuelve el worker */
  HorasProfesorResult,
  HorasProfesorSemanalResult,
  HorasAsignaturaSemanalResult,
  AsignaturaProfesoresResult,
  AsignaturaDemandaResult,
  AsignaturaAlumnosUnicosResult,
  AsignaturaRatioProfesorResult,
  AsignaturaCentroFormatoResult,
  HorasProfesoresSemanalesResult,
  AlumnosProfesorResult,
  enqueueAgendaProfesor,
  enqueueIncidenciasProfesor,
  IncidenciasProfesorResult,
  enqueueRetrasosProfesor,
  RetrasosProfesorResult,
  RetrasosAlumnoResult,
  RetrasosReservasResult,
  enqueueAsignaturasProfesor,
  AsigProfesorResult,
  BonusA365Result,
  enqueueBonusA365,
  enqueueOcupacionProfesor,
  OcupacionResult,
  enqueueHorasClienteSemanal,
  HorasClienteSemanalResult,
  AgendaProfesorResult,
  ProfesoresAlumnoResult,
  enqueueProfesoresAlumno,
  IncidenciasAlumnoResult,
  enqueueIncidenciasAlumno,
  enqueueRetrasosAlumno,
  ProgresoAlumnoResult,
  enqueueProgresoAlumno,
  AlumnosProyectoResult,
  enqueueAlumnosPorProyecto,
  TotalAlumnosUnicosResult,
  enqueueTotalAlumnosUnicos,
  enqueueTotalAlumnosUnicosSegmento,
  CancelAlumnoResult,
  enqueueCancelacionAlumno,
  HeatmapDiarioResult,
  enqueueHeatmapDiario,
  HeatHorarioResult,
  enqueueHeatmapHorario,
  TotProfesorResult,
  enqueueTotalesProfesor,
  ReservasMetricasResult,
  enqueueReservasMetricas,
  CancelTasaResult,
  enqueueCancelacionTasa,
  HeatHorarioReservasResult,
  enqueueHeatmapHorarioReservas,
  DistribReservasResult,
  enqueueReservasDistribucion,
  ReservasDiarioResult,
  enqueueReservasDiario,
  CargaFormatoResult,
  enqueueCargaFormatoProfesorCentro,
  UtilizacionCapacidadResult,
  enqueueUtilizacionCapacidadCentro,
  OcupacionCentroResult,
  enqueueOcupacionCentro,
  IncidenciasTasaResult,
  enqueueIncidenciasTasa,
  enqueueRetrasosReservas,
  enqueueFirmasDia,
  AusenciasTotalesResult,
  enqueueAusenciasTotales,
  AusenciasProfesorResult,
  enqueueAusenciasProfesor,
  VacacionesProfesorResult,
  enqueueVacacionesProfesor,
  TrainingTotalResult,
  TrainingPorTipoResult,
  TrainingImpartidasResult,
  TrainingRecibidasResult,
  TrainingRangePayload,
  enqueueTrainingTotal,
  enqueueTrainingPorTipo,
  enqueueTrainingImpartidas,
  enqueueTrainingRecibidas,
  DistribAlumnosResult,
  enqueueDistribucionAlumnos,
  HpProfesoresResult,
  HpReservasResult,
  HpReservaFirmasResult,
  enqueueHighPerfProfesores,
  enqueueHighPerfReservas,
  enqueueHighPerfReservaFirmas,
  enqueueReunionesMetricas,
  AlumnoProyectoMensualResult,
  enqueueAlumnosPorAlumno,
} from '../api/reports';
import { FirmasPeriodoResult, enqueueFirmasPeriodo } from '../api/reports';
/* ═════════════════════════════════════════════════════════════
   1. Tipos auxiliares
   ═════════════════════════════════════════════════════════════ */

/** shape devuelto por GET /reports/:id/status */
export type JobStatus<R> = {
  progress: number; // 0-100
  done: boolean; // job completed OK
  failed: boolean; // job failed
  result: null | R; // payload del worker
};

/* Enqueue genérico: acepta cualquier objeto y devuelve { jobId } */
type EnqueueFn<Payload> = (p: Payload) => Promise<{ jobId: string }>;

/* ═════════════════════════════════════════════════════════════
   2. Factoría que genera hooks tipo “useFooReport”
   ═════════════════════════════════════════════════════════════ */
function makeUseReportHook<ResultT, PayloadT>(enqueue: EnqueueFn<PayloadT>) {
  return function useReportHook() {
    const qc = useQueryClient() as QueryClient;

    /* ① Encola el job ---------------------------------------- */
    const { mutateAsync: run } = useMutation({
      mutationFn: enqueue,
      onSuccess: ({ jobId }) => {
        /* placeholder → muestra “progress 0 %” instantáneo */
        qc.setQueryData<JobStatus<ResultT>>(['reportStatus', jobId], {
          progress: 0,
          done: false,
          failed: false,
          result: null,
        });

        /* fuerza el primer refetch cuanto antes */
        qc.invalidateQueries({ queryKey: ['reportStatus', jobId] });
      },
    });

    /* ② Hook de estado (polling) ------------------------------ */
    const useStatus = (jobId?: string) =>
      useQuery<JobStatus<ResultT>>({
        queryKey: ['reportStatus', jobId],
        queryFn: () => getJobStatus(jobId!) as Promise<JobStatus<ResultT>>,
        enabled: !!jobId, // sólo si existe jobId
        /* refetch cada 2 s hasta que done/failed sea true */
        refetchInterval: (q) => (q?.state.data?.done || q?.state.data?.failed ? false : 2_000),
        staleTime: 30_000, // evita refetchs extras
      });

    return { run, useStatus };
  };
}

/* ═════════════════════════════════════════════════════════════
   3. Hooks concretos
   ═════════════════════════════════════════════════════════════ */

/* ——— informe mensual (desglose por formato / día) ——— */
export const useHorasProfesorReport = makeUseReportHook<
  HorasProfesorResult,
  { desde: string; hasta: string }
>(enqueueHorasProfesor);

/* ——— informe semanal ——— */
export const useHorasProfesorSemanalReport = makeUseReportHook<
  HorasProfesorSemanalResult,
  { desde: string; hasta: string }
>(enqueueHorasProfesorSemanal);

/* ——— informe horas semanales por asignatura ——— */
export const useHorasAsignaturaSemanalReport = makeUseReportHook<
  HorasAsignaturaSemanalResult,
  { desde: string; hasta: string }
>(enqueueHorasAsignaturaSemanal);

/* ——— informe asignaturas por nº profesores ——— */
export const useAsignaturasProfesoresReport = makeUseReportHook<AsignaturaProfesoresResult, void>(
  enqueueAsignaturasProfesores,
);

/* ——— demanda por asignatura ——— */
export const useAsignaturasDemandaReport = makeUseReportHook<
  AsignaturaDemandaResult,
  { desde: string; hasta: string; periodo: 'semanal' | 'mensual' }
>(enqueueAsignaturasDemanda);

/* ——— alumnado único por asignatura ——— */
export const useAsignaturasAlumnosUnicosReport = makeUseReportHook<
  AsignaturaAlumnosUnicosResult,
  { desde: string; hasta: string }
>(enqueueAsignaturasAlumnosUnicos);

/* ——— ratio profesor/asignatura ——— */
export const useAsignaturasRatioProfesorReport = makeUseReportHook<
  AsignaturaRatioProfesorResult,
  { desde: string; hasta: string }
>(enqueueAsignaturasRatioProfesor);

/* ——— comparativa centro/formato ——— */
export const useAsignaturasCentroFormatoReport = makeUseReportHook<
  AsignaturaCentroFormatoResult,
  { desde: string; hasta: string }
>(enqueueAsignaturasCentroFormato);

/* ——— informe horas semanales contratadas ——— */
export const useHorasProfesoresSemanalesReport = makeUseReportHook<
  HorasProfesoresSemanalesResult,
  void
>(enqueueHorasProfesoresSemanales);

/* ——— informe alumnos por profesor ——— */
export const useAlumnosProfesorReport = makeUseReportHook<
  AlumnosProfesorResult,
  { desde: string; hasta?: string }
>(enqueueAlumnosProfesor);

export const useIncidenciasProfesorReport = makeUseReportHook<
  IncidenciasProfesorResult,
  { desde: string; hasta: string }
>(enqueueIncidenciasProfesor);

export const useRetrasosProfesorReport = makeUseReportHook<
  RetrasosProfesorResult,
  { desde: string; hasta: string }
>(enqueueRetrasosProfesor);

export const useAsignaturasProfesorReport = makeUseReportHook<
  AsigProfesorResult,
  { desde: string }
>(enqueueAsignaturasProfesor);

export const useBonusA365Report = makeUseReportHook<
  BonusA365Result,
  { profesorId: number; desde: string; hasta: string }
>(enqueueBonusA365);

/* ——— informe de ocupación ——— */
export const useOcupacionProfesorReport = makeUseReportHook<
  OcupacionResult,
  {
    desde: string;
    hasta: string;
    profesorId?: number;
    centroId?: number;
    tipoId?: number;
    horaInicio?: string;
    horaFin?: string;
  }
>(enqueueOcupacionProfesor);

export const useHorasClienteSemanalReport = makeUseReportHook<
  HorasClienteSemanalResult,
  { desde: string; hasta: string }
>(enqueueHorasClienteSemanal);

export const useProfesoresAlumnoReport = makeUseReportHook<
  ProfesoresAlumnoResult,
  { desde: string; hasta?: string }
>(enqueueProfesoresAlumno);

export const useIncidenciasAlumnoReport = makeUseReportHook<
  IncidenciasAlumnoResult,
  { desde: string; hasta: string }
>(enqueueIncidenciasAlumno);

export const useRetrasosAlumnoReport = makeUseReportHook<
  RetrasosAlumnoResult,
  { desde: string; hasta: string }
>(enqueueRetrasosAlumno);

export const useTotalAlumnosUnicosReport = makeUseReportHook<
  TotalAlumnosUnicosResult,
  { desde: string; hasta: string }
>(enqueueTotalAlumnosUnicos);

export const useTotalAlumnosUnicosSegmentoReport = makeUseReportHook<
  DistribAlumnosResult,
  {
    campo: 'proyecto' | 'grade' | 'formato' | 'centro';
    valor: string;
    desde: string;
    hasta: string;
  }
>(enqueueTotalAlumnosUnicosSegmento);

export const useProgresoAlumnoReport = makeUseReportHook<
  ProgresoAlumnoResult,
  { alumnoId: number; desde: string }
>(enqueueProgresoAlumno);

export const useAlumnosProyectoReport = makeUseReportHook<
  AlumnosProyectoResult,
  { desde: string; hasta: string }
>(enqueueAlumnosPorProyecto);

export const useCancelacionAlumnoReport = makeUseReportHook<
  CancelAlumnoResult,
  { desde: string; hasta: string }
>(enqueueCancelacionAlumno);

export const useHeatmapDiarioReport = makeUseReportHook<
  HeatmapDiarioResult,
  { desde: string; hasta: string }
>(enqueueHeatmapDiario);

export const useHeatmapHorarioReport = makeUseReportHook<
  HeatHorarioResult,
  { desde: string; hasta: string; centroId?: number; tipoId?: number }
>(enqueueHeatmapHorario);

export const useTotalesProfesorReport = makeUseReportHook<TotProfesorResult, void>(
  enqueueTotalesProfesor,
);

export const useReservasMetricasReport = makeUseReportHook<
  ReservasMetricasResult,
  { desde: string; hasta: string; top?: number }
>(enqueueReservasMetricas);

export const useCancelacionTasaReport = makeUseReportHook<
  CancelTasaResult,
  { desde: string; hasta: string }
>(enqueueCancelacionTasa);

export const useHeatmapHorarioReservasReport = makeUseReportHook<
  HeatHorarioReservasResult,
  { desde: string; hasta: string }
>(enqueueHeatmapHorarioReservas);

export const useReservasDistribucionReport = makeUseReportHook<
  DistribReservasResult,
  {
    campo: 'curso' | 'proyecto' | 'formato' | 'centro' | 'estado';
    desde: string;
    hasta: string;
  }
>(enqueueReservasDistribucion);

export const useReservasDiarioReport = makeUseReportHook<
  ReservasDiarioResult,
  { desde: string; hasta: string }
>(enqueueReservasDiario);

export const useCargaFormatoReport = makeUseReportHook<
  CargaFormatoResult,
  { desde: string; hasta: string; profesorId?: number; centroId?: number }
>(enqueueCargaFormatoProfesorCentro);

export const useUtilizacionCapacidadReport = makeUseReportHook<
  UtilizacionCapacidadResult,
  { desde: string; hasta: string; centroId?: number }
>(enqueueUtilizacionCapacidadCentro);

export const useOcupacionCentroReport = makeUseReportHook<
  OcupacionCentroResult,
  { desde: string; hasta: string; centroId: number }
>(enqueueOcupacionCentro);

export const useIncidenciasTasaReport = makeUseReportHook<
  IncidenciasTasaResult,
  { desde: string; hasta: string; profesorId?: number; centroId?: number }
>(enqueueIncidenciasTasa);

export const useRetrasosReservasReport = makeUseReportHook<
  RetrasosReservasResult,
  { desde: string; hasta: string }
>(enqueueRetrasosReservas);

export const useFirmasDiaReport = makeUseReportHook<FirmasPeriodoResult, { fecha: string }>(
  enqueueFirmasDia,
);

export const useAusenciasTotalesReport = makeUseReportHook<
  AusenciasTotalesResult,
  { desde: string; hasta: string }
>(enqueueAusenciasTotales);

export const useAusenciasProfesorReport = makeUseReportHook<
  AusenciasProfesorResult,
  { desde: string; hasta: string }
>(enqueueAusenciasProfesor);

export const useVacacionesProfesorReport = makeUseReportHook<
  VacacionesProfesorResult,
  { year: number }
>(enqueueVacacionesProfesor);

export const useTrainingTotalReport = makeUseReportHook<TrainingTotalResult, TrainingRangePayload>(
  enqueueTrainingTotal,
);

export const useTrainingPorTipoReport = makeUseReportHook<
  TrainingPorTipoResult,
  TrainingRangePayload
>(enqueueTrainingPorTipo);

export const useTrainingImpartidasReport = makeUseReportHook<
  TrainingImpartidasResult,
  TrainingRangePayload
>(enqueueTrainingImpartidas);

export const useTrainingRecibidasReport = makeUseReportHook<
  TrainingRecibidasResult,
  TrainingRangePayload
>(enqueueTrainingRecibidas);

export const useDistribucionAlumnosReport = makeUseReportHook<
  DistribAlumnosResult,
  {
    campo: 'curso' | 'proyecto' | 'formato' | 'centro' | 'estado';
    valor: string;
    desde: string;
    hasta: string;
  }
>(enqueueDistribucionAlumnos);

export const useAgendaProfesorReport = makeUseReportHook<
  AgendaProfesorResult,
  { desde?: string; hasta?: string }
>(enqueueAgendaProfesor);

export { makeUseReportHook };

export const useFirmasPeriodoReport = makeUseReportHook<
  FirmasPeriodoResult,
  { desde: string; hasta?: string }
>(enqueueFirmasPeriodo);

export const useHighPerfProfesoresReport = makeUseReportHook<
  HpProfesoresResult,
  { desde: string; hasta: string }
>(enqueueHighPerfProfesores);

export const useHighPerfReservasReport = makeUseReportHook<
  HpReservasResult,
  { profesorId: number; desde: string; hasta: string }
>(enqueueHighPerfReservas);

export const useHighPerfReservaFirmasReport = makeUseReportHook<
  HpReservaFirmasResult,
  { reservaId: number }
>(enqueueHighPerfReservaFirmas);

export const useReunionesMetricasReport = makeUseReportHook<any, { desde: string; hasta: string }>(
  enqueueReunionesMetricas,
);

export const useAlumnoProyectoMensualReport = makeUseReportHook<
  AlumnoProyectoMensualResult,
  { alumnoId: number; desde: string; hasta: string }
>(async (payload) => {
  // Nos aseguramos de enviar el alumnoId
  const { alumnoId, desde, hasta } = payload;

  // Llamada real al enqueue, filtrando por alumnoId
  const { jobId } = await enqueueAlumnosPorAlumno({ alumnoId, desde, hasta });

  return { jobId };
});
